import type { IconName } from '@/shared/icons';
import type { StatusPillVariant } from '@/shared/components/ui/StatusPill';
import type { TemplateResponse, TemplateStatus } from '@troia-v3/types';
import { renderTemplateSeguro, valoresDeExemplo } from './templateRender';

/**
 * Helpers compartilhados pela tela de listagem de Templates (card + linha de tabela).
 * Toda a derivação vem de dados reais do `TemplateResponse` (providerConfig, status).
 */

export interface TemplateTypeInfo {
  icon: IconName;
  color: string;
  label: string;
}

/**
 * Ícone + cor + rótulo do tipo do template.
 * WhatsApp Gateway: derivado de `messageData.type`.
 * WhatsApp Business: derivado do formato do componente HEADER.
 * E-mail / Instagram / Messenger: derivado da família do providerType.
 */
export function getTemplateTypeInfo(template: TemplateResponse): TemplateTypeInfo {
  const providerType = template.providerConfig?.providerType;
  const pc = template.providerConfig as unknown as {
    messageData?: { type?: string };
    components?: Array<{ type?: string; format?: string }>;
  };
  const messageType = pc?.messageData?.type;
  const headerComponent = pc?.components?.find((c) => c.type === 'HEADER');

  if (providerType === 'whatsapp_gateway' && messageType) {
    const typeMap: Record<string, TemplateTypeInfo> = {
      text: { icon: 'message-square', color: 'var(--theme-primary)', label: 'Texto' },
      image: { icon: 'image', color: '#10b981', label: 'Imagem' },
      video: { icon: 'video', color: '#f59e0b', label: 'Vídeo' },
      audio: { icon: 'music', color: '#8b5cf6', label: 'Áudio' },
      document: { icon: 'file', color: '#3b82f6', label: 'Documento' },
      location: { icon: 'map-pin', color: '#ef4444', label: 'Localização' },
      contact: { icon: 'user', color: '#6366f1', label: 'Contato' },
    };
    return typeMap[messageType] || { icon: 'message-square', color: 'var(--theme-primary)', label: 'Texto' };
  }

  if (providerType === 'whatsapp_business' && headerComponent?.format) {
    const formatMap: Record<string, TemplateTypeInfo> = {
      TEXT: { icon: 'message-square', color: 'var(--theme-primary)', label: 'Texto' },
      IMAGE: { icon: 'image', color: '#10b981', label: 'Imagem' },
      VIDEO: { icon: 'video', color: '#f59e0b', label: 'Vídeo' },
      DOCUMENT: { icon: 'file', color: '#3b82f6', label: 'Documento' },
      LOCATION: { icon: 'map-pin', color: '#ef4444', label: 'Localização' },
    };
    return formatMap[headerComponent.format] || { icon: 'message-square', color: 'var(--theme-primary)', label: 'Texto' };
  }

  // Fallback por família de canal
  if (providerType && (providerType.startsWith('email') || providerType === 'gmail_api')) {
    return { icon: 'mail', color: '#6366f1', label: 'E-mail' };
  }
  if (providerType === 'instagram_direct') {
    return { icon: 'instagram', color: '#e1306c', label: 'Instagram' };
  }
  if (providerType === 'facebook_messenger') {
    return { icon: 'message-square', color: '#0084ff', label: 'Messenger' };
  }

  return { icon: 'message-square', color: 'var(--theme-primary)', label: 'Texto' };
}

/** Rótulo legível do provider. */
export function getProviderLabel(type?: string): string {
  if (!type) return '—';
  const labels: Record<string, string> = {
    whatsapp_business: 'WhatsApp Business',
    whatsapp_gateway: 'WhatsApp Gateway',
    email_smtp: 'Email SMTP',
    email_sendgrid: 'Email SendGrid',
    email_resend: 'Email Resend',
    email_ses: 'Email SES',
    gmail_api: 'Gmail',
    instagram_direct: 'Instagram Direct',
    facebook_messenger: 'Facebook Messenger',
  };
  return labels[type] || type;
}

export interface TemplateStatusMeta {
  label: string;
  /** Semântica de cor do StatusPill shared (F62) */
  variant: StatusPillVariant;
}

/** Meta do status para o StatusPill shared (F62 — pill sem borda, dot currentColor). */
export function getTemplateStatusMeta(status: TemplateStatus | string): TemplateStatusMeta {
  const map: Record<string, TemplateStatusMeta> = {
    draft: { label: 'Rascunho', variant: 'neutral' },
    pending_approval: { label: 'Pendente', variant: 'warning' },
    approved: { label: 'Aprovado', variant: 'success' },
    rejected: { label: 'Rejeitado', variant: 'error' },
    archived: { label: 'Arquivado', variant: 'neutral' },
  };
  return map[status] || { label: status || 'Desconhecido', variant: 'neutral' };
}

/** Ordem estável de status para sort client-side. */
export function getStatusRank(status: TemplateStatus | string): number {
  const order: Record<string, number> = {
    approved: 0,
    pending_approval: 1,
    draft: 2,
    rejected: 3,
    archived: 4,
  };
  return order[status] ?? 99;
}

/**
 * Texto de pré-visualização do template (resumo de uma linha no card/linha).
 * Prioriza `description`; senão sai do MESMO renderizador que desenha o balão
 * no chat, na campanha e na tela de criação — antes esta era a quarta cópia
 * do "ache o componente BODY", com regras próprias.
 *
 * E-mail mantém o assunto como resumo (é o que o card sempre mostrou).
 */
export function getTemplatePreview(template: TemplateResponse): string | undefined {
  if (template.description) return template.description;
  const render = renderTemplateSeguro(
    template.providerConfig,
    valoresDeExemplo(template.variables),
    template.variables,
  );
  if (!render) return undefined;
  if (render.canal === 'email') return render.assunto || render.corpo || undefined;
  return render.corpo || render.cabecalho?.texto || render.cabecalho?.nomeArquivo || undefined;
}
